import { useState, useCallback, useRef } from 'react';
import { useScanListener } from './useScanListener.ts';
import { useStockData } from './useStockData.ts';
import { ScanResult } from './useCameraScanner.ts';
import { ProductItemWithDetails, ExternalBarcodeLookupResponse, normalizeBarcode } from 'utils/grocyApi.ts';
import { processScannedBarcode } from 'utils/stockDataProcessing.ts';
import { provideScanFeedback } from 'utils/vibration.ts';

type PurchaseMode = 'scanning' | 'purchasing';

export function usePurchaseScanning() {
  const { barcodeMap, productItemsMap } = useStockData();
  
  const [scannedBarcode, setScannedBarcode] = useState<string>('');
  const [purchaseMode, setPurchaseMode] = useState<PurchaseMode>('scanning');
  const [scannedProduct, setScannedProduct] = useState<ProductItemWithDetails | null>(null);
  const [unknownProduct, setUnknownProduct] = useState<ExternalBarcodeLookupResponse | null>(null);
  const [barcodeNotFound, setBarcodeNotFound] = useState<string | null>(null);
  
  // Use refs to track state for immediate access in callbacks
  const purchaseModeRef = useRef<PurchaseMode>('scanning');
  const isProcessingRef = useRef(false);

  // Handle external barcode lookup for unknown products
  const handleExternalLookup = async (barcode: string) => {
    try {
      const response = await normalizeBarcode(barcode);
      
      if (response) {
        setUnknownProduct(response);
        setBarcodeNotFound(null);
        setPurchaseMode('purchasing');
        purchaseModeRef.current = 'purchasing';
        // Provide feedback on successful external lookup
        provideScanFeedback();
      } else {
        // External lookup returned null - product not found
        setBarcodeNotFound(barcode);
        setUnknownProduct(null);
        setPurchaseMode('purchasing');
        purchaseModeRef.current = 'purchasing';
      }
    } catch (error) {
      console.error('External lookup failed:', error);
      // External lookup failed - product not found
      setBarcodeNotFound(barcode);
      setUnknownProduct(null);
      setPurchaseMode('purchasing');
      purchaseModeRef.current = 'purchasing';
    }
  };

  const handleScanResult = useCallback(async (barcode: string) => {
    setScannedBarcode(barcode);
    
    // Process barcode against known products
    const result = processScannedBarcode(barcode, barcodeMap, productItemsMap);
    
    switch (result.type) {
      case 'found':
      case 'no-stock':
        setScannedProduct(result.productItem);
        setUnknownProduct(null);
        setBarcodeNotFound(null);
        setPurchaseMode('purchasing');
        purchaseModeRef.current = 'purchasing';
        // Provide feedback on successful scan
        provideScanFeedback();
        break;
        
      case 'not-found':
        setScannedProduct(null);
        setBarcodeNotFound(null);
        await handleExternalLookup(barcode);
        break;
    }
  }, [barcodeMap, productItemsMap]);

  // Listen to scan events
  useScanListener(useCallback((scanResult: ScanResult) => {
    // Only process scans when in scanning mode and not already processing
    if (purchaseModeRef.current !== 'scanning' || isProcessingRef.current) {
      return;
    }

    // Set processing flag immediately to prevent rapid-fire scans
    isProcessingRef.current = true;
    
    handleScanResult(scanResult.text).finally(() => {
      isProcessingRef.current = false;
    });
  }, [handleScanResult]));

  // Clear scan state and return to scanning mode
  const clearScan = useCallback(() => {
    setScannedBarcode('');
    setScannedProduct(null);
    setUnknownProduct(null);
    setBarcodeNotFound(null);
    setPurchaseMode('scanning');
    purchaseModeRef.current = 'scanning';
    isProcessingRef.current = false;
  }, []);

  return {
    scannedBarcode,
    purchaseMode,
    scannedProduct,
    unknownProduct,
    barcodeNotFound,
    clearScan,
  };
} 